import {
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, apiGet } from "../lib/api";
import { apiPost, apiPut } from "./api-ecrire";
import {
  type AccueilColoriage,
  accueilColoriageSchema,
  type CahierCree,
  type CahierDetail,
  type ChoixReponse,
  type ClotureApercu,
  type Comparaison,
  cahierCreeSchema,
  cahierDetailSchema,
  choixReponseSchema,
  clotureApercuSchema,
  clotureComparaisonSchema,
  clotureSchema,
  comparaisonReponseSchema,
  comparaisonSchema,
  type Demande,
  demandeReponseSchema,
  type JugementAveugle,
  type JugementReponse,
  type Juger,
  jugementReponseSchema,
  type Lancer,
  type ListeVariantes,
  listeVariantesSchema,
  type NouveauCahier,
  type NouvelleComparaison,
  type NouvelleDemande,
  type NouvelleVariante,
  type PageDetail,
  type ProchaineAveugle,
  pageDetailSchema,
  prochaineAveugleSchema,
  type Sommaire,
  type SommaireEcrit,
  sommaireReponseSchema,
  sommaireSchema,
  type Variante,
  type VarianteReponse,
  varianteReponseSchema,
} from "./contract";

/**
 * Les lectures et les ecritures du module coloriage.
 *
 * Les lectures ne reessayent pas, comme celles du portefeuille (`queries.ts`) : un 401, un
 * 403 ou un 404 est une reponse, pas une panne. Les ecritures passent par `apiPost` et
 * `apiPut` (`api-ecrire.ts`), que le bouton « C'est fait » du portefeuille partage.
 *
 * Toutes les cles de cache commencent par `coloriage` : une ecriture invalide le module
 * entier d'un coup (l'accueil compte les jugees, la grille porte les points, la page ses
 * versions), sans rien toucher au portefeuille. Seul l'apercu de la cloture est a part (voir
 * `useApercuCloture`). Un jugement a l'aveugle fait exception dans l'autre sens : il n'invalide
 * que la comparaison et l'accueil, parce qu'il **pose** lui-meme la version suivante dans le
 * cache et qu'une relecture spontanee de cette cle sauterait une version (voir
 * `useJugerAveugle`).
 */
const commun = { retry: false, refetchOnWindowFocus: false } as const;

export const CLE_COLORIAGE = ["coloriage"] as const;

/**
 * Tant qu'une demande attend ou tourne, l'accueil et la grille se relisent a ce rythme, celui
 * des sondages de l'agent : l'avancement et l'arrivee des versions se voient sans recharger.
 * Plus au-dela : la couche du Lab compte chaque GET comme une visite.
 */
export const RELECTURE_MS = 5000;

/** Une demande qui n'est pas finie : en attente de l'agent, ou en cours sur le PC. */
export function demandeActive(demande: Pick<Demande, "etat">): boolean {
  return demande.etat === "en_attente" || demande.etat === "en_cours";
}

/** Le `refetchInterval` d'une lecture qui porte des demandes. */
export function intervalleDeRelecture(
  demandes: readonly Pick<Demande, "etat">[] | undefined,
): number | false {
  return demandes?.some(demandeActive) === true ? RELECTURE_MS : false;
}

/** Les caracteres de l'empreinte que porte l'adresse d'une image : 48 bits, assez pour distinguer. */
const DEBUT_EMPREINTE = 12;

/**
 * L'adresse de l'image d'une version, servie en cache immuable par le backend. Elle porte le
 * debut de l'empreinte (`?e=`), que le serveur ignore : un identifiant reutilise apres une
 * restauration de la base change de cle de cache, et le navigateur ne ressert jamais
 * l'image d'une autre version.
 */
export function adresseImage(versionId: number, empreinte: string): string {
  return `/api/coloriage/images/${versionId}?e=${encodeURIComponent(empreinte.slice(0, DEBUT_EMPREINTE))}`;
}

/**
 * Hors demande active, l'accueil se relit quand meme toutes les 30 s : sa carte dit si
 * l'agent du PC est en ligne, et « vu il y a 3 s » fige au chargement mentirait vite. Deux
 * visites par minute, pas plus, tant que la page reste ouverte.
 */
export const RELECTURE_AGENT_MS = 30_000;

export function useAccueilColoriage(): UseQueryResult<AccueilColoriage, Error> {
  return useQuery({
    queryKey: [...CLE_COLORIAGE, "accueil"],
    queryFn: () => apiGet("/api/coloriage", accueilColoriageSchema),
    refetchInterval: (query) =>
      intervalleDeRelecture(query.state.data?.demandes) || RELECTURE_AGENT_MS,
    ...commun,
  });
}

export function useCahierColoriage(id: string, actif = true): UseQueryResult<CahierDetail, Error> {
  return useQuery({
    queryKey: [...CLE_COLORIAGE, "cahier", id],
    queryFn: () => apiGet(`/api/coloriage/cahiers/${encodeURIComponent(id)}`, cahierDetailSchema),
    enabled: actif,
    refetchInterval: (query) => intervalleDeRelecture(query.state.data?.demandes),
    ...commun,
  });
}

/**
 * Ce que clore le cahier effacerait. Lu a chaque ouverture de la boite de confirmation
 * (`gcTime: 0` : rien n'est garde une fois la boite fermee), et hors de la cle `coloriage` :
 * la cloture elle-meme invalide le module, et relirait sinon l'apercu d'un cahier deja clos.
 */
export function useApercuCloture(id: string): UseQueryResult<ClotureApercu, Error> {
  return useQuery({
    queryKey: ["coloriage-cloture", id],
    queryFn: () =>
      apiGet(`/api/coloriage/cahiers/${encodeURIComponent(id)}/cloture`, clotureApercuSchema),
    gcTime: 0,
    ...commun,
  });
}

export function usePageColoriage(id: string, actif = true): UseQueryResult<PageDetail, Error> {
  return useQuery({
    queryKey: [...CLE_COLORIAGE, "page", id],
    queryFn: () => apiGet(`/api/coloriage/pages/${encodeURIComponent(id)}`, pageDetailSchema),
    enabled: actif,
    ...commun,
  });
}

/** Le message a afficher sous les boutons quand une ecriture est refusee. */
export function messageDEchec(error: unknown): string {
  return error instanceof ApiError ? error.message : "L'écriture n'a pas abouti.";
}

/**
 * Pose la reponse dans la page en cache tout de suite, pour que le bouton presse et le point
 * changent sans attendre la relecture ; puis invalide tout le module, qui se relit.
 */
function remplacerDansLaPage(
  client: QueryClient,
  pageId: string,
  modifier: (page: PageDetail) => PageDetail,
): void {
  client.setQueryData<PageDetail>([...CLE_COLORIAGE, "page", pageId], (page) =>
    page === undefined ? page : modifier(page),
  );
}

export type DemandeDeJugement = { readonly versionId: number } & Juger;

/** `POST /api/coloriage/versions/{id}/jugement`. */
export function useJuger(
  pageId: string,
): UseMutationResult<JugementReponse, Error, DemandeDeJugement> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, jugement, note }: DemandeDeJugement) =>
      apiPost(
        `/api/coloriage/versions/${versionId}/jugement`,
        { jugement, note },
        jugementReponseSchema,
      ),
    onSuccess: (reponse) => {
      remplacerDansLaPage(client, pageId, (page) => ({
        ...page,
        versions: page.versions.map((version) =>
          version.id === reponse.version.id ? reponse.version : version,
        ),
      }));
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

/** `POST /api/coloriage/pages/{id}/choix` ; `version` a null retire le choix. */
export function useChoisir(
  pageId: string,
): UseMutationResult<ChoixReponse, Error, { version: number | null }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ version }: { version: number | null }) =>
      apiPost(
        `/api/coloriage/pages/${encodeURIComponent(pageId)}/choix`,
        { version },
        choixReponseSchema,
      ),
    onSuccess: (reponse) => {
      remplacerDansLaPage(client, pageId, (page) => ({
        ...page,
        page: { ...page.page, ...reponse },
      }));
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

export type DemandeReponse = { readonly demande: Demande };

/** `POST /api/coloriage/demandes` : rendre N versions des pages cochees, ou monter le PDF. */
export function useNouvelleDemande(): UseMutationResult<DemandeReponse, Error, NouvelleDemande> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (demande: NouvelleDemande) =>
      apiPost("/api/coloriage/demandes", demande, demandeReponseSchema),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

/**
 * Pose une demande qui vient de changer d'etat dans les lectures en cache, sans attendre leur
 * relecture : la ligne de l'accueil change tout de suite, et la grille, qui ne montre que les
 * demandes qui tournent, retire celle qui ne tourne plus.
 */
function poserLaDemande(client: QueryClient, demande: Demande): void {
  client.setQueriesData<AccueilColoriage>({ queryKey: [...CLE_COLORIAGE, "accueil"] }, (accueil) =>
    accueil === undefined
      ? accueil
      : {
          ...accueil,
          demandes: accueil.demandes.map((ancienne) =>
            ancienne.id === demande.id ? demande : ancienne,
          ),
        },
  );
  client.setQueriesData<CahierDetail>({ queryKey: [...CLE_COLORIAGE, "cahier"] }, (detail) =>
    detail === undefined
      ? detail
      : {
          ...detail,
          demandes: detail.demandes
            .map((ancienne) => (ancienne.id === demande.id ? demande : ancienne))
            .filter(demandeActive),
        },
  );
}

export type ActionSurDemande = { readonly id: number; readonly action: "annuler" | "relancer" };

/**
 * `POST /api/coloriage/demandes/{id}/annuler` (en attente ou en cours) et `.../relancer`
 * (echouee ou annulee : elle repart en attente, les versions deja arrivees sont gardees).
 * Aucun corps : un objet vide part quand meme, pour que la requete porte un
 * `Content-Type: application/json` comme les autres ecritures. Ces routes n'ont pas la garde
 * `corpsJson` (elles ne lisent rien) : ce corps est ignore.
 */
export function useActionSurDemande(): UseMutationResult<DemandeReponse, Error, ActionSurDemande> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: ActionSurDemande) =>
      apiPost(`/api/coloriage/demandes/${id}/${action}`, {}, demandeReponseSchema),
    onSuccess: (reponse) => {
      poserLaDemande(client, reponse.demande);
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

export type BilanCloture = { readonly images_effacees: number; readonly octets_liberes: number };

/** `POST /api/coloriage/cahiers/{id}/clore`, sans corps (voir `useActionSurDemande`). */
export function useClore(id: string): UseMutationResult<BilanCloture, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost(`/api/coloriage/cahiers/${encodeURIComponent(id)}/clore`, {}, clotureSchema),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

// ---------------------------------------------------------------------------------------
// Les variantes et les comparaisons a l'aveugle (ticket 4, #153).

export function useVariantes(): UseQueryResult<ListeVariantes, Error> {
  return useQuery({
    queryKey: [...CLE_COLORIAGE, "variantes"],
    queryFn: () => apiGet("/api/coloriage/variantes", listeVariantesSchema),
    ...commun,
  });
}

/**
 * Une comparaison. Tant qu'elle est ouverte et qu'il reste des jugements, elle se relit au
 * rythme des sondages de l'agent : ses versions arrivent du PC pendant ce temps-la, et
 * l'avancement se voit sans recharger. Une comparaison toute jugee ou close ne bouge plus.
 */
export function useComparaison(id: string, actif = true): UseQueryResult<Comparaison, Error> {
  return useQuery({
    queryKey: [...CLE_COLORIAGE, "comparaison", id],
    queryFn: () =>
      apiGet(`/api/coloriage/comparaisons/${encodeURIComponent(id)}`, comparaisonSchema),
    enabled: actif,
    refetchInterval: (query) => {
      const lue = query.state.data;
      return lue !== undefined && lue.etat === "ouverte" && lue.jugees < lue.total
        ? RELECTURE_MS
        : false;
    },
    ...commun,
  });
}

/** La cle de la prochaine version a juger a l'aveugle, posee a la main apres un jugement. */
export function cleProchaineAveugle(id: string): readonly unknown[] {
  return [...CLE_COLORIAGE, "aveugle", id];
}

/**
 * Pose une variante ecrite dans la liste en cache, avant que la relecture n'arrive, comme
 * `remplacerDansLaPage` et `poserLaDemande` le font pour une page et une demande.
 *
 * Sans cela, l'ecran ouvre une variante que la liste lue ne contient pas encore : une copie
 * tout juste creee (« Dupliquer ») s'affichait en formulaire vide, et l'enregistrer renvoyait
 * des phrases nulles, donc effacait la copie. Une variante inconnue s'ajoute, une variante
 * connue se remplace (une archivee sort alors de la liste des vivantes toute seule).
 */
function poserLaVariante(client: QueryClient, variante: Variante): void {
  client.setQueriesData<ListeVariantes>({ queryKey: [...CLE_COLORIAGE, "variantes"] }, (liste) => {
    if (liste === undefined) return liste;
    const connue = liste.variantes.some((une) => une.id === variante.id);
    return {
      ...liste,
      variantes: connue
        ? liste.variantes.map((une) => (une.id === variante.id ? variante : une))
        : [...liste.variantes, variante],
    };
  });
}

/**
 * La prochaine version a juger a l'aveugle. Jamais relue d'elle-meme : c'est le jugement qui
 * pose la suivante dans le cache (`useJugerAveugle`), et une relecture spontanee sauterait une
 * version sous l'oeil de Nicolas.
 */
export function useProchaineAveugle(
  id: string,
  actif = true,
): UseQueryResult<ProchaineAveugle, Error> {
  return useQuery({
    queryKey: cleProchaineAveugle(id),
    queryFn: () =>
      apiGet(
        `/api/coloriage/comparaisons/${encodeURIComponent(id)}/prochaine`,
        prochaineAveugleSchema,
      ),
    enabled: actif,
    ...commun,
  });
}

/** `POST /api/coloriage/variantes` : la variante que Nicolas vient d'ecrire. */
export function useNouvelleVariante(): UseMutationResult<VarianteReponse, Error, NouvelleVariante> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variante: NouvelleVariante) =>
      apiPost("/api/coloriage/variantes", variante, varianteReponseSchema),
    onSuccess: (reponse) => {
      poserLaVariante(client, reponse.variante);
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

export type ModificationDeVariante = { readonly id: number } & NouvelleVariante;

/** `PUT /api/coloriage/variantes/{id}` : refuse par le backend si la variante est figee. */
export function useModifierVariante(): UseMutationResult<
  VarianteReponse,
  Error,
  ModificationDeVariante
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...variante }: ModificationDeVariante) =>
      apiPut(`/api/coloriage/variantes/${id}`, variante, varianteReponseSchema),
    onSuccess: (reponse) => {
      poserLaVariante(client, reponse.variante);
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

export type ActionSurVariante = {
  readonly id: number;
  readonly action: "dupliquer" | "archiver";
};

/**
 * `POST /api/coloriage/variantes/{id}/dupliquer` (une figee redevient modifiable) et
 * `.../archiver` (elle sort de la liste sans s'effacer). Sans corps, comme les actions d'une
 * demande : un objet vide part quand meme, pour le `Content-Type`.
 */
export function useActionSurVariante(): UseMutationResult<
  VarianteReponse,
  Error,
  ActionSurVariante
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: ActionSurVariante) =>
      apiPost(`/api/coloriage/variantes/${id}/${action}`, {}, varianteReponseSchema),
    onSuccess: (reponse) => {
      // La copie d'une duplication n'est dans aucune lecture : sans ce depot, l'ecran l'ouvrirait
      // vide et l'enregistrer effacerait ses phrases.
      poserLaVariante(client, reponse.variante);
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

export type ComparaisonReponse = {
  readonly comparaison: Comparaison;
  readonly demande: Demande;
};

/** `POST /api/coloriage/comparaisons` : la comparaison et la demande de rendu qu'elle pose. */
export function useNouvelleComparaison(): UseMutationResult<
  ComparaisonReponse,
  Error,
  NouvelleComparaison
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (comparaison: NouvelleComparaison) =>
      apiPost("/api/coloriage/comparaisons", comparaison, comparaisonReponseSchema),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

/**
 * `POST /api/coloriage/comparaisons/{id}/jugement` : la reponse porte la version suivante,
 * posee tout de suite dans le cache pour que l'ecran enchaine sans aller-retour. Puis le
 * module s'invalide, la comparaison comprise : son avancement bouge a chaque jugement.
 */
export function useJugerAveugle(
  id: string,
): UseMutationResult<ProchaineAveugle, Error, JugementAveugle> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (jugement: JugementAveugle) =>
      apiPost(
        `/api/coloriage/comparaisons/${encodeURIComponent(id)}/jugement`,
        jugement,
        prochaineAveugleSchema,
      ),
    onSuccess: (reponse) => {
      client.setQueryData<ProchaineAveugle>(cleProchaineAveugle(id), reponse);
      void client.invalidateQueries({ queryKey: [...CLE_COLORIAGE, "comparaison", id] });
      void client.invalidateQueries({ queryKey: [...CLE_COLORIAGE, "accueil"] });
    },
  });
}

/** `POST /api/coloriage/comparaisons/{id}/clore`, sans corps : les images partent. */
export function useCloreComparaison(id: string): UseMutationResult<BilanCloture, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost(
        `/api/coloriage/comparaisons/${encodeURIComponent(id)}/clore`,
        {},
        clotureComparaisonSchema,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

// ---------------------------------------------------------------------------------------
// Creer depuis la console (#271) : un brouillon, son sommaire, son lancement.

/** `POST /api/coloriage/cahiers` : un brouillon ne dans la console ; 409 `cle_prise`. */
export function useCreerCahier(): UseMutationResult<CahierCree, Error, NouveauCahier> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (nouveau: NouveauCahier) =>
      apiPost("/api/coloriage/cahiers", nouveau, cahierCreeSchema),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

/**
 * Le sommaire d'un cahier : son brief, sa version, ses pages. Il se relit au rythme de l'agent
 * tant qu'une demande attend ou tourne sur le cahier : quand Claude ecrira le sommaire (#273),
 * l'ecran verra la nouvelle version sans recharger.
 */
export function useSommaire(id: string, actif = true): UseQueryResult<Sommaire, Error> {
  return useQuery({
    queryKey: [...CLE_COLORIAGE, "sommaire", id],
    queryFn: () =>
      apiGet(`/api/coloriage/cahiers/${encodeURIComponent(id)}/sommaire`, sommaireSchema),
    enabled: actif,
    refetchInterval: (query) => intervalleDeRelecture(query.state.data?.demandes),
    ...commun,
  });
}

export type SommaireEnregistre = { readonly version: number };

/**
 * `PUT /api/coloriage/cahiers/{id}/sommaire` : tout le sommaire, avec la version qu'il
 * remplace ; 409 `sommaire_change` quand un autre ecran (ou Claude) a ecrit entre-temps, 409
 * `pas_un_brouillon` quand le cahier est deja lance.
 */
export function useEcrireSommaire(
  id: string,
): UseMutationResult<SommaireEnregistre, Error, SommaireEcrit> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (sommaire: SommaireEcrit) =>
      apiPut(
        `/api/coloriage/cahiers/${encodeURIComponent(id)}/sommaire`,
        sommaire,
        sommaireReponseSchema,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}

/**
 * `POST /api/coloriage/cahiers/{id}/lancer` : le brouillon devient un cahier ouvert et sa
 * demande de rendu part vers l'agent du PC ; 400 `sommaire_vide` ou `sommaire_incomplet`, 409
 * `pas_un_brouillon`. Tout le module se relit : la page du cahier passe a la grille.
 */
export function useLancerBrouillon(id: string): UseMutationResult<DemandeReponse, Error, Lancer> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (lancer: Lancer) =>
      apiPost(
        `/api/coloriage/cahiers/${encodeURIComponent(id)}/lancer`,
        lancer,
        demandeReponseSchema,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
    },
  });
}
